import React, { createContext, useContext, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

export interface LocationEntry {
  pathname: string;
  search: string;
  hash: string;
}

interface NavigationHistoryContextType {
  getLastNonExcludedLocation: (excludedPaths?: string[]) => LocationEntry | null;
  getStepsToGoBackToPath: (targetPath: string) => number;
  cleanHistoryStack: (stepsToRemove: number) => void;
}

const NavigationHistoryContext = createContext<NavigationHistoryContextType | null>(null);

interface NavigationHistoryProviderProps {
  children: React.ReactNode;
}

export function NavigationHistoryProvider({ children }: NavigationHistoryProviderProps) {
  const location = useLocation();
  const historyStack = useRef<LocationEntry[]>([]);

  useEffect(() => {
    const newLocation: LocationEntry = {
      pathname: location.pathname,
      search: location.search,
      hash: location.hash
    };

    // Avoiding adding duplicate consecutive locations
    const lastEntry = historyStack.current[historyStack.current.length - 1];
    if (
      !lastEntry ||
      lastEntry.pathname !== newLocation.pathname ||
      lastEntry.search !== newLocation.search ||
      lastEntry.hash !== newLocation.hash
    ) {
      historyStack.current.push(newLocation);
    }
  }, [location.pathname, location.search, location.hash]);

  const getLastNonExcludedLocation = (excludedPaths: string[] = []): LocationEntry | null => {
    const shouldExcludePath = (pathname: string): boolean => {
      return excludedPaths.some(path => pathname.includes(path));
    };

    // Finding the last non-excluded location in the stack, here considering there will be at least 2 locations(present one and one to go back to) 
    // if in any case there only one entry and need to go back to that only, then need to modify the loop condition
    for (let i = historyStack.current.length - 2; i >= 0; i--) {
      const entry = historyStack.current[i];
      if (!shouldExcludePath(entry.pathname)) {
        return entry;
      }
    }
    return null;
  };

  const getStepsToGoBackToPath = (targetPath: string): number => {
    for (let i = historyStack.current.length - 2; i >= 0; i--) {
      const entry = historyStack.current[i];
      
      if (entry.pathname.includes(targetPath)) {
        return historyStack.current.length - 1 - i;
      }
      
      if (entry.search) {
        const searchParams = new URLSearchParams(entry.search);
        for (const [, value] of searchParams.entries()) {
          const decodedValue = decodeURIComponent(value);
          if (decodedValue === targetPath || decodedValue.includes(targetPath) || targetPath.includes(decodedValue)) {
            return historyStack.current.length - 1 - i;
          }
        }
      }
      
      if (entry.hash && entry.hash.includes(targetPath)) {
        return historyStack.current.length - 1 - i;
      }
    }
    return 1;
  };

  const cleanHistoryStack = (stepsToRemove: number): void => {
    if (stepsToRemove > 0 && stepsToRemove < historyStack.current.length) {
      historyStack.current = historyStack.current.slice(0, -stepsToRemove);
    }
  };

  const contextValue: NavigationHistoryContextType = {
    getLastNonExcludedLocation,
    getStepsToGoBackToPath,
    cleanHistoryStack
  };

  return (
    <NavigationHistoryContext.Provider value={contextValue}>
      {children}
    </NavigationHistoryContext.Provider>
  );
}

export function useNavigationHistory(): NavigationHistoryContextType {
  const context = useContext(NavigationHistoryContext);
  if (!context) {
    throw new Error('useNavigationHistory must be used within NavigationHistoryProvider');
  }
  return context;
}

